import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlaneDeparture, FaUser, FaCalendarAlt, FaMapMarkerAlt, FaPlane, FaBuilding, FaExchangeAlt, FaRegCopy } from 'react-icons/fa';
import { podeAbrirRota } from './PrivateRoute';
import { usePermissoes } from '../context/PermissoesContext';
import { ehRestrita } from '../util/acessoPermissao';
import { getAuthPerfil } from '../util/authSession';
import { copiarTexto } from '../util/util';

const BRAND_NAVY   = '#1f3c88';
const BRAND_BLUE   = '#2d7ff9';
const BRAND_SKY    = '#eef6ff';
const BRAND_BORDER = '#dbe8f8';

// Cor do número de cada indicador. Mesmas famílias das pills do `.fin-pp` (warn, ok, info),
// para o número do cabeçalho e o badge da linha falarem a mesma cor.
const TOM_INDICADOR = {
    warn: { cor: '#9a6b00', fundo: '#fff7e6', borda: '#f3d9a4' },
    ok:   { cor: '#15803d', fundo: '#ecf8f0', borda: '#bfe3cb' },
    info: { cor: BRAND_NAVY, fundo: BRAND_SKY, borda: BRAND_BORDER },
    danger: { cor: '#c1232b', fundo: '#fcebeb', borda: '#f3c2c4' },
};

// Indicador numérico: o número grande é o que se lê de longe; o rótulo diz do quê.
function Indicador({ rotulo, valor, titulo, tom }) {
    const t = TOM_INDICADOR[tom] ?? { cor: BRAND_NAVY, fundo: '#fff', borda: BRAND_BORDER };
    return (
        <div title={titulo} style={{ background: t.fundo, border: `1px solid ${t.borda}`, borderRadius: 12, padding: '8px 14px', minWidth: 0 }}>
            <div className="fw-bold" style={{ color: t.cor, fontSize: 20, lineHeight: 1.1 }}>{valor ?? 0}</div>
            <div className="text-uppercase text-truncate" style={{ color: '#5f6f8a', fontSize: 11, letterSpacing: '0.05em', fontWeight: 600, marginTop: 2 }}>
                {rotulo}
            </div>
        </div>
    );
}

// Item compacto da faixa de stats: rótulo (ícone + texto) + valor ('—' quando vazio).
function Stat({ icon, label, value }) {
    const texto = (value === 0 || value) ? String(value) : '';
    return (
        <div style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-1 text-uppercase" style={{ color: '#7b8ba7', fontSize: 11, letterSpacing: '0.05em', fontWeight: 600 }}>
                {icon}{label}
            </div>
            <div className="text-truncate" style={{ color: BRAND_NAVY, fontSize: 14, marginTop: 3, fontWeight: 600 }} title={texto}>
                {texto || '—'}
            </div>
        </div>
    );
}

// Item do painel pendurado: rótulo, valor e o copiar, porque o Aéreo cola o valor na reserva.
function ItemPainel({ icone, rotulo, valor }) {
    const texto = String(valor ?? '').trim();
    const copiar = async () => {
        if (await copiarTexto(texto)) toast.success(`${rotulo} copiado.`);
        else toast.error('Não foi possível copiar.');
    };
    return (
        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
            <span className="d-flex align-items-center gap-1 text-uppercase" style={{ color: '#7b8ba7', fontSize: 11, letterSpacing: '0.05em', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {icone}{rotulo}
            </span>
            <span className="text-truncate" style={{ color: BRAND_NAVY, fontSize: 14, fontWeight: 600 }} title={texto}>{texto || '—'}</span>
            {texto && (
                <button type="button" onClick={copiar} title={`Copiar ${rotulo}`} aria-label={`Copiar ${rotulo}`}
                    className="btn btn-sm border-0 bg-transparent shadow-none p-0 d-inline-flex" style={{ color: BRAND_BLUE }}>
                    <FaRegCopy size={13} />
                </button>
            )}
        </div>
    );
}

/**
 * Cabeçalho enxuto compartilhado pelas telas de Aéreo (Solicitação e Importação de
 * Malha). Substitui o hero antigo por uma faixa compacta com os dados essenciais do
 * evento. Campos vazios viram '—'.
 *
 * `tktMedio` é do PEDIDO: tela de uma solicitação passa o valor (null aparece como '—');
 * tela do evento inteiro (Solicitações, Importação das Opções) não passa a prop, e o bloco some — não
 * há um teto só para mostrar, e '—' diria que falta preencher.
 *
 * `atalhos`: lista de { rotulo, titulo, to, desabilitado } para as outras telas do fluxo. Com `to`
 * (o caminho), o atalho é link: Ctrl+Click, o botão do meio e "abrir em nova aba" funcionam, e o
 * clique normal navega na mesma aba. `onClick` fica para atalho sem destino. `atalho` (um só)
 * continua aceito. `destaque` pinta o atalho de azul e `icone` troca o ícone: é o botão de ação da
 * tela ao lado da navegação (o atendimento do Aéreo). Atalho com `to` para rota que o perfil não
 * abre (`podeAbrirRota`) não aparece: o clique só o devolveria para o painel.
 *
 * `voosSolicitados` segue a regra do TKT: sem a prop, o bloco some (tela onde a contagem
 * não se aplica).
 *
 * `indicadores`: lista de { rotulo, valor, titulo, tom: 'warn' | 'ok' | 'info' | 'danger' } — contagens
 * da tela em números grandes, abaixo dos dados do evento.
 *
 * `painel`: lista de { rotulo, valor, icone } numa faixa presa embaixo do card, mais estreita que ele
 * (o Centro de Custo e a Ordem Interna das telas de emissão). Cada valor tem o copiar.
 */
export default function CabecalhoEventoAereo({
    titulo, nomeEvento, cliente, solicitante, dataEvento, localEvento, tktMedio, voosSolicitados,
    atalho, atalhos, indicadores, painel,
}) {
    const itensPainel = (painel ?? []).filter(Boolean);
    const tkt = (tktMedio === 0 || tktMedio) ? String(tktMedio) : '';
    const aplicaTkt = tktMedio !== undefined;
    const aplicaVoos = voosSolicitados !== undefined;
    const numeros = (indicadores ?? []).filter(Boolean);
    const permissoes = usePermissoes();
    const botoes = (atalhos ?? (atalho ? [atalho] : [])).filter(Boolean)
        .filter(a => !a.to || (podeAbrirRota(getAuthPerfil(a.to), String(a.to).split(/[?#]/)[0]) && (!ehRestrita(a.to) || permissoes.temAcesso(a.to))));

    // O topo cabe numa linha, com o título inteiro, os atalhos e o TKT médio? A largura natural vem
    // de uma cópia invisível sem quebra, comparada com a da linha. Não cabendo, os atalhos descem
    // para a linha de baixo, à direita, e o TKT fica na linha do título: o flex-wrap sozinho
    // quebraria primeiro o TKT, o último item, ou o deixaria vazar.
    const linhaRef = useRef(null);
    const medidaRef = useRef(null);
    const [atalhosEmbaixo, setAtalhosEmbaixo] = useState(false);
    useLayoutEffect(() => {
        const linha = linhaRef.current;
        const medida = medidaRef.current;
        const medir = () => setAtalhosEmbaixo(medida.offsetWidth > linha.clientWidth);
        medir();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observador = new ResizeObserver(medir);
        observador.observe(linha);
        observador.observe(medida);
        return () => observador.disconnect();
    }, []);

    const blocoTitulo = (
        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 12, background: BRAND_SKY }}>
                <FaPlaneDeparture size={20} style={{ color: BRAND_BLUE }} />
            </div>
            <div style={{ minWidth: 0 }}>
                <div className="fw-bold text-uppercase" style={{ color: BRAND_BLUE, fontSize: 11, letterSpacing: '0.1em' }}>{titulo}</div>
                <div className="fw-bold text-truncate" style={{ color: BRAND_NAVY, fontSize: 20, lineHeight: 1.2 }} title={nomeEvento || ''}>
                    {nomeEvento || 'Evento'}
                </div>
            </div>
        </div>
    );
    // Atalhos para as outras telas de Aéreo do MESMO evento. Ficam no cabeçalho porque é ele que as
    // telas compartilham. Com destino, link (abre em nova aba com Ctrl+Click); desabilitado ou sem
    // destino, botão — link não desabilita.
    const blocoAtalhos = (estilo) => botoes.length > 0 && (
        <div className="d-flex align-items-center gap-3 flex-wrap justify-content-end" style={estilo}>
            {botoes.map(b => {
                const classe = 'btn btn-sm d-inline-flex align-items-center gap-2 fw-semibold';
                // `destaque`: ação da tela ao lado da navegação (o atendimento do Aéreo), em azul.
                const aparencia = b.destaque
                    ? { background: BRAND_BLUE, border: `1px solid ${BRAND_BLUE}`, color: '#fff', borderRadius: 999, padding: '6px 14px', opacity: b.desabilitado ? 0.55 : 1 }
                    : { background: '#fff', border: `1px solid ${BRAND_BORDER}`, color: BRAND_NAVY, borderRadius: 999, padding: '6px 14px', opacity: b.desabilitado ? 0.55 : 1 };
                const conteudo = <>{b.icone ?? <FaExchangeAlt size={11} />}{b.rotulo}</>;
                return b.to && !b.desabilitado ? (
                    <Link key={b.rotulo} to={b.to} title={b.titulo} className={classe} style={{ ...aparencia, textDecoration: 'none' }}>
                        {conteudo}
                    </Link>
                ) : (
                    <button key={b.rotulo} type="button" onClick={b.onClick} title={b.titulo}
                        disabled={!!b.desabilitado} className={classe} style={aparencia}>
                        {conteudo}
                    </button>
                );
            })}
        </div>
    );
    const blocoTkt = (estilo) => aplicaTkt && (
        <div className="text-end ps-3" style={{ borderLeft: `1px solid ${BRAND_SKY}`, ...estilo }}>
            <div className="text-uppercase" style={{ color: '#7b8ba7', fontSize: 11, letterSpacing: '0.08em', fontWeight: 600 }}>TKT médio</div>
            <div className="fw-bold" style={{ color: BRAND_NAVY, fontSize: 20 }}>{tkt || '—'}</div>
        </div>
    );

    return (
        <div className="page-title mb-3">
            <div className="bg-white" style={{ border: `1px solid ${BRAND_BORDER}`, borderRadius: 14, boxShadow: '0 8px 22px rgba(31,60,136,0.06)', padding: '16px 20px' }}>

                {/* Topo: ícone + título da tela + nome do evento; atalhos e TKT médio à direita.
                    Quebrados, eles continuam à direita. */}
                <div ref={linhaRef} className="d-flex align-items-center gap-3 flex-wrap">
                    {blocoTitulo}
                    {!atalhosEmbaixo && blocoAtalhos({ marginLeft: 'auto', flexShrink: 0 })}
                    {blocoTkt(atalhosEmbaixo || !botoes.length ? { marginLeft: 'auto' } : undefined)}
                    {atalhosEmbaixo && (
                        <div className="d-flex justify-content-end" style={{ flexBasis: '100%' }}>
                            {blocoAtalhos()}
                        </div>
                    )}
                </div>
                {/* Cópia invisível do topo numa linha só, para medir. A caixa de largura zero corta
                    o transbordo, que senão abriria rolagem na página. */}
                <div aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', visibility: 'hidden' }}>
                    <div ref={medidaRef} className="d-flex align-items-center gap-3" style={{ width: 'max-content' }}>
                        {blocoTitulo}
                        {blocoAtalhos()}
                        {blocoTkt()}
                    </div>
                </div>

                <div style={{ height: 1, background: BRAND_SKY, margin: '14px 0' }} />

                {/* Faixa de stats — auto-fit, quebra conforme a largura */}
                <div className="d-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 16 }}>
                    <Stat icon={<FaBuilding size={12} />}      label="Cliente" value={cliente} />
                    <Stat icon={<FaUser size={12} />}          label="Solicitante" value={solicitante} />
                    <Stat icon={<FaCalendarAlt size={12} />}   label="Data" value={dataEvento} />
                    <Stat icon={<FaMapMarkerAlt size={12} />}  label="Local" value={localEvento} />
                    {aplicaVoos && <Stat icon={<FaPlane size={12} />} label="Voos solicitados" value={voosSolicitados} />}
                </div>

                {numeros.length > 0 && (
                    <div className="d-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginTop: 14 }}>
                        {numeros.map(n => <Indicador key={n.rotulo} {...n} />)}
                    </div>
                )}

            </div>

            {/* Painel pendurado: sai de baixo do card, sem borda em cima e mais estreito, para ler como
                parte dele. Fica sempre aberto: é dado de consulta, não seção. */}
            {itensPainel.length > 0 && (
                <div className="d-flex flex-wrap align-items-center" style={{
                    margin: '0 18px', padding: '9px 18px', gap: '6px 32px', justifyContent: 'space-evenly',
                    background: 'linear-gradient(180deg, #f7fbff 0%, #fff 100%)',
                    border: `1px solid ${BRAND_BORDER}`, borderTop: 'none', borderRadius: '0 0 12px 12px',
                }}>
                    {itensPainel.map(p => <ItemPainel key={p.rotulo} {...p} />)}
                </div>
            )}
        </div>
    );
}
